import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import type { z } from 'zod';
import {
  FundBusinessUpsertMutationParamsSchema,
  FundBusinessesModel,
} from '../projects/_models/fund-business';
import { queryKeyMap } from '../review-sheet/utils/query-keys';

export const useFundBusinesses = <T = z.infer<typeof FundBusinessesModel>>({
  projectId,
  select,
  enabled = true,
}: {
  projectId: string | undefined;
  select?: (data: z.infer<typeof FundBusinessesModel>) => T;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: queryKeyMap.fundBusiness(projectId),
    queryFn: async () => {
      const fundBusinesses = await axiosGet(
        `api/projects/${projectId}/fund-businesses/`,
      );

      return FundBusinessesModel.parse(fundBusinesses);
    },
    select,
    enabled: !!projectId && enabled,
  });
};

const _useCreateFundBusiness = (projectId: string) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof FundBusinessUpsertMutationParamsSchema>,
    ) => {
      const payload = FundBusinessUpsertMutationParamsSchema.parse(values);
      const res = await axiosPost(
        `/api/projects/${projectId}/fund-businesses/`,
        payload,
      );
      await invalidate(queryKeyMap.fundBusiness(projectId));

      return res;
    },
  });
};

const _useDeleteFundBusiness = (projectId: string) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (fundBusinessId: string) => {
      const res = await axiosDelete(
        `/api/projects/${projectId}/fund-businesses/${fundBusinessId}/`,
      );
      await invalidate(queryKeyMap.fundBusiness(projectId));

      return res;
    },
  });
};

const _useUpdateFundBusiness = (projectId: string) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      fundBusinessId,
      values,
    }: {
      fundBusinessId: string;
      values: z.infer<typeof FundBusinessUpsertMutationParamsSchema>;
    }) => {
      const payload = FundBusinessUpsertMutationParamsSchema.parse(values);
      const res = await axiosPatch(
        `/api/projects/${projectId}/fund-businesses/${fundBusinessId}/`,
        payload,
      );
      await invalidate(queryKeyMap.fundBusiness(projectId));

      return res;
    },
  });
};
